import React, { useState, useEffect, useCallback } from 'react';
import { CheckSquare, X, CheckCircle, Circle, ChevronRight, ChevronLeft, Clock } from 'lucide-react';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

interface ChecklistItem {
  id: number;
  title: string;
  order_index: number;
}

interface TodayChecklist {
  id: number;
  title: string;
  description: string | null;
  frequency: 'daily' | 'weekly' | 'monthly';
  items: ChecklistItem[];
}

const STORAGE_KEY = 'checklist_popup_last_date';

const ChecklistPopup: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [checklists, setChecklists] = useState<TodayChecklist[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [checked, setChecked] = useState<Record<number, Record<number, boolean>>>({});
  const [visible, setVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dismissed, setDismissed] = useState<Set<number>>(new Set());

  const today = new Date().toISOString().slice(0, 10);

  const fetchTodayChecklists = useCallback(async () => {
    if (!user || !['admin', 'superadmin'].includes(user.role)) return;

    // Aynı gün tekrar gösterme kontrolü (her checklist için ayrı)
    const lastDate = localStorage.getItem(STORAGE_KEY);
    if (lastDate === today) return;

    try {
      const res = await apiService.getTodayChecklists();
      const list: TodayChecklist[] = res.checklists || [];
      if (list.length === 0) {
        localStorage.setItem(STORAGE_KEY, today);
        return;
      }

      // Başlangıç checked durumu
      const initialChecked: Record<number, Record<number, boolean>> = {};
      list.forEach(cl => {
        initialChecked[cl.id] = {};
        cl.items.forEach(item => {
          initialChecked[cl.id][item.id] = false;
        });
      });

      setChecklists(list);
      setChecked(initialChecked);
      setCurrentIndex(0);
      setVisible(true);
    } catch (error) {
      console.error('ChecklistPopup fetch error:', error);
    }
  }, [user, today]);

  useEffect(() => {
    // Auth yüklenince bekle, sonra kontrol et
    const timer = setTimeout(fetchTodayChecklists, 1500);
    return () => clearTimeout(timer);
  }, [fetchTodayChecklists]);

  const toggleItem = (checklistId: number, itemId: number) => {
    setChecked(prev => ({
      ...prev,
      [checklistId]: {
        ...prev[checklistId],
        [itemId]: !prev[checklistId]?.[itemId],
      }
    }));
  };

  const handleDismiss = async (checklistId: number, isCompleted: boolean) => {
    setSubmitting(true);
    const cl = checklists.find(c => c.id === checklistId);
    if (!cl) { setSubmitting(false); return; }

    const itemCompletions = cl.items.map(item => ({
      item_id: item.id,
      is_completed: checked[checklistId]?.[item.id] || false,
    }));

    const allCompleted = itemCompletions.every(ic => ic.is_completed);

    try {
      await apiService.saveChecklistLog({
        checklist_id: checklistId,
        log_date: today,
        is_completed: allCompleted,
        item_completions: itemCompletions,
      });
    } catch (error) {
      console.error('ChecklistPopup save error:', error);
    } finally {
      setSubmitting(false);
    }

    const newDismissed = new Set(dismissed).add(checklistId);
    setDismissed(newDismissed);

    const remaining = checklists.filter(c => !newDismissed.has(c.id));
    if (remaining.length === 0) {
      localStorage.setItem(STORAGE_KEY, today);
      setVisible(false);
    } else {
      const nextIndex = Math.min(currentIndex, remaining.length - 1);
      setCurrentIndex(nextIndex);
    }
  };

  const handleSkip = async (checklistId: number) => {
    await handleDismiss(checklistId, false);
  };

  if (!visible || checklists.length === 0) return null;

  const activeLists = checklists.filter(c => !dismissed.has(c.id));
  if (activeLists.length === 0) return null;

  const cl = activeLists[Math.min(currentIndex, activeLists.length - 1)];
  if (!cl) return null;

  const itemChecks = checked[cl.id] || {};
  const checkedCount = Object.values(itemChecks).filter(Boolean).length;
  const totalCount = cl.items.length;
  const allChecked = checkedCount === totalCount && totalCount > 0;
  const progress = totalCount > 0 ? (checkedCount / totalCount) * 100 : 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[80] p-4 pb-20 md:pb-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary/20 dark:bg-primary/40 rounded-lg flex items-center justify-center">
              <CheckSquare className="w-4 h-4 text-primary dark:text-primary" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                {t('checklist.popupHeader')}
              </p>
              <p className="text-xs text-gray-400">
                {activeLists.length > 1 && `${activeLists.indexOf(cl) + 1} / ${activeLists.length}`}
                {' '}
                <span className={`font-medium ${
                  cl.frequency === 'daily' ? 'text-green-600' :
                  cl.frequency === 'weekly' ? 'text-primary' : 'text-purple-600'
                }`}>
                  {t(`checklist.freq${cl.frequency.charAt(0).toUpperCase() + cl.frequency.slice(1)}` as any)}
                </span>
              </p>
            </div>
          </div>
          <button
            onClick={() => handleSkip(cl.id)}
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
            title={t('checklist.popupSkip')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">{cl.title}</h2>
          {cl.description && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{cl.description}</p>
          )}

          {/* Progress */}
          <div className="mb-4">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>{checkedCount}/{totalCount} {t('checklist.popupProgress')}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
              <div
                className="bg-primary h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Items */}
          <div className="space-y-2.5 max-h-64 overflow-y-auto pr-1">
            {cl.items.map(item => {
              const isChecked = itemChecks[item.id] || false;
              return (
                <button
                  key={item.id}
                  onClick={() => toggleItem(cl.id, item.id)}
                  className={`w-full flex items-start gap-3 p-3 rounded-xl border transition-all text-left ${
                    isChecked
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                      : 'bg-gray-50 dark:bg-gray-750 border-gray-200 dark:border-gray-700 hover:border-primary/50 hover:bg-primary/10 dark:hover:bg-primary/10'
                  }`}
                >
                  {isChecked ? (
                    <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                  ) : (
                    <Circle className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" />
                  )}
                  <span className={`text-sm ${
                    isChecked
                      ? 'text-green-700 dark:text-green-400 line-through'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}>
                    {item.title}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2">
          <button
            onClick={() => handleSkip(cl.id)}
            disabled={submitting}
            className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            {t('checklist.popupSkip')}
          </button>

          <div className="flex items-center gap-2">
            {/* Navigate between multiple checklists */}
            {activeLists.length > 1 && (
              <>
                <button
                  onClick={() => setCurrentIndex(i => Math.max(0, i - 1))}
                  disabled={currentIndex === 0}
                  className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-30 rounded-lg"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setCurrentIndex(i => Math.min(activeLists.length - 1, i + 1))}
                  disabled={currentIndex === activeLists.length - 1}
                  className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-30 rounded-lg"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </>
            )}

            <button
              onClick={() => handleDismiss(cl.id, allChecked)}
              disabled={submitting}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 ${
                allChecked
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-primary hover:bg-primary-dark text-white'
              }`}
            >
              <CheckSquare className="w-4 h-4" />
              {submitting ? t('checklist.popupSaving') : allChecked ? t('checklist.popupCompleted') : t('checklist.popupSaveClose')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChecklistPopup;
